import React from 'react'
import errorReporter from './errorReporter'
import ErrorFallback from './ErrorFallback'

export interface ErrorBoundaryProps {
  children: React.ReactNode
  fallbackVariant: 'app' | 'route'
  pathname?: string
  onReset?: () => void
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  retryCount: number
  prevPathname?: string
}

const MAX_RETRIES = 3

class ErrorBoundaryCore extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    const { pathname } = props
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
      prevPathname: pathname,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.pathname && state.prevPathname && props.pathname !== state.prevPathname) {
      return {
        hasError: false,
        error: null,
        retryCount: 0,
        prevPathname: props.pathname,
      }
    }
    if (props.pathname !== state.prevPathname) {
      return { prevPathname: props.pathname }
    }
    return null
  }

  handleRetry = (): void => {
    const { onReset } = this.props
    this.setState(prevState => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1,
    }))

    if (onReset) {
      onReset()
    }
  }

  handleGoHome = (): void => {
    window.location.href = '/apps'
  }

  handleReload = (): void => {
    window.location.reload()
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { fallbackVariant, onError } = this.props
    errorReporter.report(error, errorInfo, fallbackVariant)

    if (onError) {
      onError(error, errorInfo)
    }
  }

  render(): React.ReactNode {
    const { hasError, error, retryCount } = this.state
    const { children, fallbackVariant } = this.props

    if (hasError) {
      return (
        <ErrorFallback
          variant={fallbackVariant}
          error={error}
          retryCount={retryCount}
          maxRetries={MAX_RETRIES}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          onReload={this.handleReload}
        />
      )
    }

    return children
  }
}

export default ErrorBoundaryCore
